import { Link } from 'wouter';
import { BUSINESS } from '@mopani/contracts';

/**
 * Public footer.
 *
 * Preserved from the measured baseline (§1.5, P-8): navy #0b2d4d, padding
 * 64px 0 32px, four columns — Quick Links / Service Areas / Contact / CTA —
 * tagline, copyright and the "B2B Procurement for the Mopani Region" line.
 *
 * CORRECTED (C-1, C-2, C-3): the phone number, the WhatsApp link and the email
 * address all come from BUSINESS in @mopani/contracts. The July regression put
 * tel:0729655486 in 12 places because each component inlined its own copy; there
 * is now exactly one definition.
 */

const QUICK_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About Us' },
  { href: '/for-schools', label: 'For Schools' },
  { href: '/catalogue', label: 'Catalogue' },
  { href: '/partners', label: 'Become a Partner' },
] as const;

const FOOTER_AREAS = [
  'Phalaborwa',
  'Namakgale',
  'Lulekani',
  'Makhushane',
  'Mashishimale',
  'Wider Mopani Region',
] as const;

export function Footer() {
  return (
    <footer className="section-navy pb-8 pt-16 text-white">
      <div className="container-content">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            {/* Measured from production: logo-mark 32x32 + wordmark 60x20. */}
            <div className="mb-4 flex items-center gap-2">
              <img
                src="/assets/logo-mark.png"
                alt=""
                width={32}
                height={32}
                className="h-8 w-8 object-contain"
              />
              <img
                src="/assets/wordmark.png"
                alt="Mopani Suppliers"
                width={60}
                height={20}
                className="h-5 w-[60px] object-contain brightness-0 invert"
              />
            </div>
            <p className="font-heading text-lg font-semibold text-white">
              Supplying Schools.
              <br />
              Supporting Communities.
            </p>
            <p className="mt-3 text-sm text-white/70">
              Your trusted local procurement partner for reliable hygiene and cleaning
              supplies in the Mopani region.
            </p>
          </div>

          <nav aria-labelledby="footer-quick-links">
            <h2 id="footer-quick-links" className="font-heading text-lg font-semibold text-white">
              Quick Links
            </h2>
            <ul className="mt-4 space-y-2">
              {QUICK_LINKS.map((l) => (
                <li key={l.href}>
                  <Link href={l.href} className="text-sm text-white/80 hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="font-heading text-lg font-semibold text-white">Service Areas</h2>
            <ul className="mt-4 space-y-2">
              {FOOTER_AREAS.map((a) => (
                <li key={a} className="text-sm text-white/80">
                  {a}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-lg font-semibold text-white">Contact</h2>
            <ul className="mt-4 space-y-3">
              <li className="text-sm text-white/80">
                {BUSINESS.town}, {BUSINESS.province}, {BUSINESS.postalCode}
              </li>
              <li className="flex items-center gap-3">
                <a
                  href={`tel:${BUSINESS.phone}`}
                  className="text-sm text-white/80 hover:text-white"
                >
                  {BUSINESS.phoneDisplay} (Call)
                </a>
              </li>
              <li className="flex items-center gap-3">
                {/* C-2: a real WhatsApp deep link. Production labelled a tel: link
                    "Call/WhatsApp" — tapping it started a phone call. */}
                <a
                  href={BUSINESS.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/80 hover:text-white"
                >
                  WhatsApp {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="text-sm text-white/80 hover:text-white"
                >
                  {BUSINESS.email}
                </a>
              </li>
            </ul>
            <Link href="/quote" className="btn btn-inverse btn-small mt-5">
              Request Quote
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-white/15 pt-6 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Mopani Suppliers. All rights reserved.</p>
          <p>B2B Procurement for the Mopani Region</p>
        </div>
      </div>
    </footer>
  );
}
